import { useCallback, useEffect, useRef, useState } from "react";
import { BellRing, Loader2 } from "lucide-react";
import {
  subscribeAlertRemote,
  unsubscribeAlertRemote,
  fetchAlertSubscriptionsRemote,
} from "@/lib/agentcore";
import { getIdToken } from "@/lib/auth";

/**
 * AlertsToggle - a labeled, accessible switch that subscribes / unsubscribes the
 * signed-in user to SEC 8-K filing alerts for one competitor (email + Slack).
 *
 * State is either supplied by the parent (when it already has the subscription
 * list, to avoid N list calls) OR fetched once on mount. Toggling calls the
 * subscribe / unsubscribe helpers, optimistically flips, and reverts with a calm
 * inline note if the backend refuses (e.g. a private / non-US company that does
 * not file 8-Ks) or on a network failure. Identity + the email/webhook live
 * server-side; nothing sensitive is sent from here.
 */

interface AlertsToggleProps {
  competitor: string;
  /** Whether the user is already subscribed. When undefined, the component
   *  fetches its own initial state via fetchAlertSubscriptionsRemote on mount. */
  initialOn?: boolean;
  /** Whether the user has a connected Slack channel (so alerts also reach Slack). */
  slackConnected?: boolean;
  /** Whether the backend monitor that fires these alerts is enabled. When false,
   *  subscribing still works but we add a subtle "activates once enabled" note. */
  monitorEnabled?: boolean;
}

type Note = { kind: "info" | "error" | "ok"; text: string } | null;

export function AlertsToggle({ competitor, initialOn, slackConnected, monitorEnabled }: AlertsToggleProps) {
  // When the parent passed an explicit initial state we trust it and skip the
  // mount fetch; otherwise we resolve it ourselves.
  const selfFetch = initialOn === undefined;
  const [on, setOn] = useState<boolean>(initialOn ?? false);
  const [monitorOn, setMonitorOn] = useState<boolean>(monitorEnabled ?? true);
  const [busy, setBusy] = useState<boolean>(selfFetch); // loading while we resolve initial state
  const [note, setNote] = useState<Note>(null);

  const noteTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flashNote = useCallback((n: Note, ms = 4000) => {
    setNote(n);
    if (noteTimer.current) clearTimeout(noteTimer.current);
    if (n && n.kind !== "error") {
      noteTimer.current = setTimeout(() => setNote(null), ms);
    }
  }, []);
  useEffect(() => () => { if (noteTimer.current) clearTimeout(noteTimer.current); }, []);

  // Keep in sync if the parent updates the supplied props (reactive watchlist).
  useEffect(() => { if (initialOn !== undefined) setOn(initialOn); }, [initialOn]);
  useEffect(() => { if (monitorEnabled !== undefined) setMonitorOn(monitorEnabled); }, [monitorEnabled]);

  // Self-resolve initial state on mount when the parent did not supply it.
  useEffect(() => {
    if (!selfFetch) return;
    let cancelled = false;
    (async () => {
      const token = getIdToken();
      if (!token) { if (!cancelled) setBusy(false); return; }
      const res = await fetchAlertSubscriptionsRemote(token);
      if (cancelled) return;
      if (res.ok) {
        const match = res.subscriptions.some(
          (s) => s.trim().toLowerCase() === competitor.trim().toLowerCase(),
        );
        setOn(match);
        setMonitorOn(res.monitorEnabled);
      }
      setBusy(false);
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitor]);

  const toggle = useCallback(async () => {
    if (busy || !competitor.trim()) return;
    const token = getIdToken();
    if (!token) {
      flashNote({ kind: "error", text: "Please sign in again to manage alerts." });
      return;
    }
    const next = !on;
    setBusy(true);
    setNote(null);
    // Optimistic flip - revert on failure.
    setOn(next);

    if (next) {
      const res = await subscribeAlertRemote(token, competitor);
      setBusy(false);
      if (!res.ok) {
        setOn(false); // revert: not a public US SEC filer, or a transient failure
        flashNote({
          kind: "error",
          text: res.error || "We couldn't turn on alerts for this company right now.",
        });
        return;
      }
      // Prefer the backend's slackLinked; fall back to the parent-supplied prop.
      const linked = typeof res.slackLinked === "boolean" ? res.slackLinked : slackConnected;
      const slackHint = linked === false
        ? " Connect Slack to also get these in your channel."
        : "";
      const monitorHint = !monitorOn ? " Alerts activate once monitoring is enabled." : "";
      flashNote({
        kind: "ok",
        text: `You'll get an alert when ${competitor} files a high-signal 8-K.${slackHint}${monitorHint}`,
      });
    } else {
      const res = await unsubscribeAlertRemote(token, competitor);
      setBusy(false);
      if (!res.ok) {
        setOn(true); // revert
        flashNote({
          kind: "error",
          text: res.error || "We couldn't turn off alerts right now. Please try again.",
        });
        return;
      }
      flashNote({ kind: "ok", text: `Alerts for ${competitor} are off.` });
    }
  }, [busy, competitor, on, monitorOn, slackConnected, flashNote]);

  const subtext =
    `Get an email the moment ${competitor} files a high-signal SEC 8-K - a leadership ` +
    "change, M&A, funding, a material cybersecurity incident, or financial distress. " +
    "Sent to your Slack too when connected.";

  return (
    <div className="rounded-xl border border-[#1a2640] bg-[#070d1a] p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-2.5 min-w-0">
          <span className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-lg border border-brand-600/30 bg-brand-700/20 text-brand-300 shrink-0">
            <BellRing className="w-3.5 h-3.5" />
          </span>
          <div className="min-w-0">
            <p id={`alerts-toggle-label-${slug(competitor)}`} className="text-sm font-medium text-slate-200">
              SEC filing alerts
            </p>
            <p className="mt-0.5 text-[11px] text-slate-500 leading-relaxed">{subtext}</p>
            {!monitorOn && on && (
              <p className="mt-1.5 text-[11px] text-slate-500 leading-relaxed">
                Alerts activate once monitoring is enabled.
              </p>
            )}
          </div>
        </div>

        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-busy={busy}
          aria-labelledby={`alerts-toggle-label-${slug(competitor)}`}
          onClick={toggle}
          disabled={busy}
          className={`relative shrink-0 w-12 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-500/50 focus:ring-offset-2 focus:ring-offset-[#070d1a] disabled:cursor-wait min-h-[44px] min-w-[44px] flex items-center px-1 ${
            on ? "bg-brand-500" : "bg-[#1a2640]"
          } ${busy ? "opacity-70" : "cursor-pointer"}`}
        >
          <span
            className="flex h-5 w-5 items-center justify-center rounded-full bg-white shadow transition-transform duration-200"
            style={{ transform: on ? "translateX(20px)" : "translateX(0)" }}
          >
            {busy && <Loader2 className="w-3 h-3 animate-spin text-slate-500" />}
          </span>
        </button>
      </div>

      {note && (
        <p
          role={note.kind === "error" ? "alert" : "status"}
          className={`mt-3 text-[11px] leading-relaxed ${
            note.kind === "error"
              ? "text-amber-300"
              : note.kind === "ok"
                ? "text-emerald-300/90"
                : "text-slate-400"
          }`}
        >
          {note.text}
        </p>
      )}
    </div>
  );
}

/** Stable id fragment from a competitor name for label association. */
function slug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "competitor";
}
